import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { AssetRecord, AssetFolder, AssetType } from '../types';
import {
  compressAsset,
  loadCompressionProfile,
  type CompressionProfile,
  type CompressionResult,
} from '../lib/assetCompression';

const BUCKET = 'cs-assets';

export interface UploadProgress {
  id: string;
  fileName: string;
  status: 'compressing' | 'uploading' | 'saving' | 'complete' | 'error';
  originalSize: number;
  compressedSize: number;
  message?: string;
}

interface AssetState {
  assets: AssetRecord[];
  folders: AssetFolder[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  typeFilter: AssetType | null;
  activeFolderId: string | null;
  favoritesOnly: boolean;
  canDelete: boolean;
  uploadQueue: UploadProgress[];

  loadAssets: () => Promise<void>;
  loadFolders: () => Promise<void>;
  refreshUserRole: () => Promise<void>;

  uploadAsset: (file: File, folderId?: string | null) => Promise<AssetRecord | null>;
  uploadMany: (files: File[], folderId?: string | null) => Promise<AssetRecord[]>;

  deleteAsset: (id: string) => Promise<boolean>;
  renameAsset: (id: string, name: string) => Promise<boolean>;
  moveAssetToFolder: (id: string, folderId: string | null) => Promise<boolean>;
  toggleFavorite: (id: string) => Promise<boolean>;
  incrementUsage: (id: string) => Promise<void>;

  createFolder: (name: string, parentId?: string | null) => Promise<AssetFolder | null>;
  renameFolder: (id: string, name: string) => Promise<boolean>;
  deleteFolder: (id: string) => Promise<boolean>;

  setSearchQuery: (q: string) => void;
  setTypeFilter: (t: AssetType | null) => void;
  setActiveFolderId: (id: string | null) => void;
  setFavoritesOnly: (v: boolean) => void;
  clearError: () => void;
  clearCompletedUploads: () => void;
}

function newUploadId(): string {
  return `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function inferAssetType(file: File): AssetType {
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) return 'svg';
  return 'image';
}

async function checkAdminRole(userId: string | null): Promise<boolean> {
  if (!userId) return false;
  const { data } = await supabase
    .from('tb_users')
    .select('platform_role')
    .eq('auth_id', userId)
    .maybeSingle();
  const role = data?.platform_role;
  return role === 'admin' || role === 'super_admin' || role === 'platform_owner';
}

export const useAssetStore = create<AssetState>((set, get) => ({
  assets: [],
  folders: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  typeFilter: null,
  activeFolderId: null,
  favoritesOnly: false,
  canDelete: false,
  uploadQueue: [],

  loadAssets: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cs_assets')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      set({ assets: (data as AssetRecord[]) || [], isLoading: false });
    } catch (err) {
      console.error('[assetStore] loadAssets failed', err);
      set({ error: 'Failed to load assets', isLoading: false });
    }
  },

  loadFolders: async () => {
    try {
      const { data, error } = await supabase
        .from('cs_asset_folders')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      set({ folders: (data as AssetFolder[]) || [] });
    } catch (err) {
      console.error('[assetStore] loadFolders failed', err);
    }
  },

  refreshUserRole: async () => {
    const { data } = await supabase.auth.getUser();
    const canDelete = await checkAdminRole(data.user?.id ?? null);
    set({ canDelete });
  },

  uploadAsset: async (file, folderId = null) => {
    const queueId = newUploadId();
    set((s) => ({
      uploadQueue: [
        ...s.uploadQueue,
        {
          id: queueId,
          fileName: file.name,
          status: 'compressing',
          originalSize: file.size,
          compressedSize: 0,
        },
      ],
    }));

    try {
      const profile: CompressionProfile = loadCompressionProfile();
      const result: CompressionResult = await compressAsset(file, profile);

      set((s) => ({
        uploadQueue: s.uploadQueue.map((u) =>
          u.id === queueId
            ? { ...u, status: 'uploading', compressedSize: result.compressedSize }
            : u
        ),
      }));

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id ?? 'anon';
      const assetId = crypto.randomUUID();

      const mainPath = `library/${userId}/${assetId}/main.${result.mainExt}`;
      const thumbPath = `library/${userId}/${assetId}/thumb.webp`;

      const { error: mainErr } = await supabase.storage
        .from(BUCKET)
        .upload(mainPath, result.mainBlob, { contentType: result.mainMime, upsert: true });
      if (mainErr) throw mainErr;

      const { error: thumbErr } = await supabase.storage
        .from(BUCKET)
        .upload(thumbPath, result.thumbnailBlob, { contentType: 'image/webp', upsert: true });
      if (thumbErr) throw thumbErr;

      const { data: thumbUrl } = supabase.storage.from(BUCKET).getPublicUrl(thumbPath);

      set((s) => ({
        uploadQueue: s.uploadQueue.map((u) =>
          u.id === queueId ? { ...u, status: 'saving' } : u
        ),
      }));

      const record = {
        id: assetId,
        name: file.name.replace(/\.[^.]+$/, ''),
        file_name: file.name,
        file_size: result.compressedSize,
        original_file_size: result.originalSize,
        compressed: result.compressed,
        mime_type: result.mainMime,
        storage_path: mainPath,
        thumbnail_url: thumbUrl.publicUrl,
        category: inferAssetType(file),
        asset_type: result.type,
        tags: [] as string[],
        width: result.width,
        height: result.height,
        folder_id: folderId,
        svg_markup: result.inlineSvgMarkup ?? null,
        is_favorite: false,
        usage_count: 0,
        created_by: userData.user?.id || null,
      };

      const { data, error } = await supabase
        .from('cs_assets')
        .insert(record)
        .select()
        .maybeSingle();
      if (error) throw error;

      set((s) => ({
        assets: data ? [data as AssetRecord, ...s.assets] : s.assets,
        uploadQueue: s.uploadQueue.map((u) =>
          u.id === queueId ? { ...u, status: 'complete' } : u
        ),
      }));

      return (data as AssetRecord) || null;
    } catch (err) {
      console.error('[assetStore] uploadAsset failed', err);
      const message = err instanceof Error ? err.message : 'Upload failed';
      set((s) => ({
        uploadQueue: s.uploadQueue.map((u) =>
          u.id === queueId ? { ...u, status: 'error', message } : u
        ),
        error: message,
      }));
      return null;
    }
  },

  uploadMany: async (files, folderId = null) => {
    const created: AssetRecord[] = [];
    for (const file of files) {
      const record = await get().uploadAsset(file, folderId);
      if (record) created.push(record);
    }
    return created;
  },

  deleteAsset: async (id) => {
    try {
      const asset = get().assets.find((a) => a.id === id);
      if (asset) {
        const prefix = asset.storage_path.split('/').slice(0, -1).join('/');
        await supabase.storage.from(BUCKET).remove([
          asset.storage_path,
          `${prefix}/thumb.webp`,
        ]);
      }
      const { error } = await supabase.from('cs_assets').delete().eq('id', id);
      if (error) throw error;
      set((s) => ({ assets: s.assets.filter((a) => a.id !== id) }));
      return true;
    } catch (err) {
      console.error('[assetStore] deleteAsset failed', err);
      set({ error: 'Failed to delete asset. Admins only.' });
      return false;
    }
  },

  renameAsset: async (id, name) => {
    try {
      const { error } = await supabase
        .from('cs_assets')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      set((s) => ({
        assets: s.assets.map((a) => (a.id === id ? { ...a, name } : a)),
      }));
      return true;
    } catch {
      set({ error: 'Failed to rename asset' });
      return false;
    }
  },

  moveAssetToFolder: async (id, folderId) => {
    try {
      const { error } = await supabase
        .from('cs_assets')
        .update({ folder_id: folderId, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      set((s) => ({
        assets: s.assets.map((a) => (a.id === id ? { ...a, folder_id: folderId } : a)),
      }));
      return true;
    } catch {
      set({ error: 'Failed to move asset' });
      return false;
    }
  },

  toggleFavorite: async (id) => {
    const asset = get().assets.find((a) => a.id === id);
    if (!asset) return false;
    const next = !asset.is_favorite;
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, is_favorite: next } : a)),
    }));
    try {
      const { error } = await supabase
        .from('cs_assets')
        .update({ is_favorite: next, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return true;
    } catch {
      set((s) => ({
        assets: s.assets.map((a) => (a.id === id ? { ...a, is_favorite: !next } : a)),
      }));
      return false;
    }
  },

  incrementUsage: async (id) => {
    const asset = get().assets.find((a) => a.id === id);
    if (!asset) return;
    const nextCount = (asset.usage_count || 0) + 1;
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, usage_count: nextCount } : a)),
    }));
    try {
      await supabase
        .from('cs_assets')
        .update({ usage_count: nextCount })
        .eq('id', id);
    } catch {
      // non-critical
    }
  },

  createFolder: async (name, parentId = null) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cs_asset_folders')
        .insert({
          name,
          parent_id: parentId,
          created_by: userData.user?.id || null,
        })
        .select()
        .maybeSingle();
      if (error) throw error;
      if (data) {
        set((s) => ({ folders: [...s.folders, data as AssetFolder] }));
      }
      return (data as AssetFolder) || null;
    } catch {
      set({ error: 'Failed to create folder' });
      return null;
    }
  },

  renameFolder: async (id, name) => {
    try {
      const { error } = await supabase
        .from('cs_asset_folders')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      set((s) => ({
        folders: s.folders.map((f) => (f.id === id ? { ...f, name } : f)),
      }));
      return true;
    } catch {
      return false;
    }
  },

  deleteFolder: async (id) => {
    try {
      const { error } = await supabase.from('cs_asset_folders').delete().eq('id', id);
      if (error) throw error;
      set((s) => ({
        folders: s.folders.filter((f) => f.id !== id),
        assets: s.assets.map((a) => (a.folder_id === id ? { ...a, folder_id: null } : a)),
      }));
      return true;
    } catch {
      set({ error: 'Failed to delete folder. Admins only.' });
      return false;
    }
  },

  setSearchQuery: (q) => set({ searchQuery: q }),
  setTypeFilter: (t) => set({ typeFilter: t }),
  setActiveFolderId: (id) => set({ activeFolderId: id }),
  setFavoritesOnly: (v) => set({ favoritesOnly: v }),
  clearError: () => set({ error: null }),
  clearCompletedUploads: () =>
    set((s) => ({
      uploadQueue: s.uploadQueue.filter(
        (u) => u.status !== 'complete' && u.status !== 'error'
      ),
    })),
}));

export function getAssetPublicUrl(storagePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}
